import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { money } from "@/lib/money";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CaretDown, CaretUp, PaperPlaneTilt, ShieldCheck, Handshake } from "@phosphor-icons/react";
import { CompWeeksTable } from "@/components/CompWeeksTable";

const hrs = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });

function GuardrailsCard({ canEdit }) {
  const [g, setG] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings/sales-guardrails").then((r) => setG(r.data || { min_bill_rate: 0, max_pay_to_bill_pct: 0 })); }, []);
  if (!g) return null;
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.put("/settings/sales-guardrails", { min_bill_rate: parseFloat(g.min_bill_rate) || 0, max_pay_to_bill_pct: parseFloat(g.max_pay_to_bill_pct) || 0 });
      if (!data) throw new Error("Not allowed");
      toast.success("Guardrails saved.");
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || err.message); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="bg-white border border-slate-200 rounded-md p-5 mb-6" data-testid="sales-guardrails-card">
      <div className="flex items-center gap-2 mb-1"><ShieldCheck size={20} weight="duotone" className="text-emerald-600" /><h2 className="font-black text-lg">Guardrails on rep drafts</h2></div>
      <p className="text-sm text-slate-500 mb-4">Enforced when a rep submits a proposal or packet (0 = no limit). Admin-authored proposals and packets are never checked.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Minimum bill rate ($/hr)</label>
          <input type="number" step="0.01" min="0" value={g.min_bill_rate} onChange={(e) => setG({ ...g, min_bill_rate: e.target.value })} disabled={!canEdit} data-testid="guardrail-min-bill"
                 className="mt-1 w-full h-10 px-3 border border-slate-300 rounded-md font-mono disabled:bg-slate-50" />
          <div className="text-xs text-slate-400 mt-1">Proposals below this are rejected.</div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Max pay as % of bill rate</label>
          <input type="number" step="0.1" min="0" max="100" value={g.max_pay_to_bill_pct} onChange={(e) => setG({ ...g, max_pay_to_bill_pct: e.target.value })} disabled={!canEdit} data-testid="guardrail-pay-pct"
                 className="mt-1 w-full h-10 px-3 border border-slate-300 rounded-md font-mono disabled:bg-slate-50" />
          <div className="text-xs text-slate-400 mt-1">Packet pay rate can't exceed this share of the project's bill rate.</div>
        </div>
        {canEdit && <button disabled={busy} data-testid="guardrail-save" className="h-10 px-4 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-md disabled:opacity-50">{busy ? "Saving…" : "Save guardrails"}</button>}
      </div>
    </form>
  );
}

function RepWeeks({ repId }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/sales-reps/${repId}/summary?weeks=12`).then((r) => setD(r.data)); }, [repId]);
  if (!d) return <div className="text-sm text-slate-500 p-4">Loading weeks…</div>;
  return (
    <div className="p-4 bg-slate-50 border-t border-slate-200" data-testid={`rep-weeks-${repId}`}>
      <div className="text-xs text-slate-500 mb-2">Last 12 weeks · YTD {hrs(d.ytd.hours)} hrs · {money(d.ytd.pay)} pay · {d.ytd.weeks_over_break_even} weeks over break-even</div>
      <CompWeeksTable weeks={d.weeks} testId={`rep-weeks-table-${repId}`} />
      {d.projects.length > 0 && (
        <div className="mt-3 text-xs text-slate-600">
          <b>Projects:</b> {d.projects.map((p) => `${p.client_name} — ${p.name}${p.status !== "active" ? ` (${p.status})` : ""}`).join(" · ")}
        </div>
      )}
    </div>
  );
}

export default function SalesReps() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [reps, setReps] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const load = () => api.get("/sales-reps").then((r) => setReps(Array.isArray(r.data) ? r.data : []));
  useEffect(() => { load(); }, []);

  const sendLogin = async (r) => {
    if (!window.confirm(`Email ${r.name} their sales portal login${r.email ? ` at ${r.email}` : ""}?`)) return;
    setBusyId(r.id);
    try {
      const { data } = await api.post(`/workers/${r.id}/sales-rep/send-login`);
      if (!data) throw new Error("Not allowed");
      toast.success(`Login ${data.login_created ? "created and " : ""}emailed to ${data.email}.`); load();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || err.message); }
    finally { setBusyId(null); }
  };

  const toggleReview = async (r) => {
    const next = !r.review_required;
    if (!window.confirm(next
      ? `Require your approval again for ${r.name}'s proposals and assignment packets?`
      : `Turn OFF approval for ${r.name}?\n\nTheir proposals and assignment packets will go straight to clients and workers. You'll get an FYI email each time.`)) return;
    setBusyId(r.id);
    try {
      const { data } = await api.put(`/workers/${r.id}/sales-rep`, { is_sales_rep: true, rate_per_hour: r.rate_per_hour, weekly_draw: r.weekly_draw, review_required: next });
      if (!data) throw new Error("Not allowed");
      toast.success(next ? `${r.name}'s drafts now wait for your approval.` : `${r.name} can now send directly — you'll be notified each time.`); load();
    } catch (err) { toast.error(err.response?.data?.detail?.toString() || err.message); }
    finally { setBusyId(null); }
  };

  const pendingProposals = (reps || []).reduce((n, r) => n + (r.pending_proposals || 0), 0);
  const pendingPackets = (reps || []).reduce((n, r) => n + (r.pending_packets || 0), 0);
  const pendingTotal = pendingProposals + pendingPackets;

  return (
    <div className="p-4 md:p-8">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="tinylabel">Sales &amp; hiring</div>
          <h1 className="text-3xl font-black">Sales Reps</h1>
          <p className="text-slate-500 mt-1 text-sm max-w-2xl">Reps are paid weekly: the greater of their draw or approved hours on their projects × their rate. Flag a worker as a rep from <Link to="/workers" className="underline">Workers → Sales rep</Link>; credit projects to them on the Project form or the proposal.</p>
        </div>
        {pendingTotal > 0 && (
          <div className="flex gap-2" data-testid="sales-reps-pending-link">
            {pendingProposals > 0 && <Link to="/proposals?filter=review" data-testid="sales-reps-pending-proposals" className="h-11 px-4 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-md flex items-center gap-2">{pendingProposals} proposal{pendingProposals > 1 ? "s" : ""} to review</Link>}
            {pendingPackets > 0 && <Link to="/packets?filter=review" data-testid="sales-reps-pending-packets" className="h-11 px-4 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-md flex items-center gap-2">{pendingPackets} packet{pendingPackets > 1 ? "s" : ""} to review</Link>}
          </div>
        )}
      </div>

      <GuardrailsCard canEdit={isAdmin} />

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]" data-testid="sales-reps-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left px-4 py-3">Rep</th>
              <th className="text-left px-4 py-3">Comp plan</th>
              <th className="text-left px-4 py-3">This week</th>
              <th className="text-left px-4 py-3">Last 3 weeks</th>
              <th className="text-right px-4 py-3">Projects · Workers</th>
              <th className="text-left px-4 py-3">Approval</th>
              <th className="text-left px-4 py-3">Drafts</th>
              <th className="text-left px-4 py-3">Portal login</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {reps === null && <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>}
            {reps?.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400">
                <Handshake size={36} weight="duotone" className="mx-auto mb-2 text-slate-300" />
                No sales reps yet. Open <Link to="/workers" className="underline font-semibold">Workers</Link>, click <b>Sales rep</b> on the person, set their rate and draw, then send them a login.
              </td></tr>
            )}
            {reps?.map((r) => {
              const cw = r.current_week || {};
              const over = cw.applies === "commission";
              const open = openId === r.id;
              return (
                <Fragment key={r.id}>
                  <tr className={`border-t border-slate-100 hover:bg-slate-50 ${r.status !== "active" ? "opacity-60" : ""}`} data-testid={`sales-rep-row-${r.id}`}>
                    <td className="px-4 py-3">
                      <Link to={`/workers/${r.id}`} className="font-semibold text-slate-900 hover:text-emerald-700">{r.name}</Link>
                      <div className="text-xs text-slate-500">{r.email || "no email"}{r.status !== "active" ? ` · ${r.status}` : ""}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-700">
                      <div><span className="font-mono">{money(r.rate_per_hour)}</span>/hr · <span className="font-mono">{money(r.weekly_draw)}</span> draw</div>
                      <div className="text-slate-500">break-even {hrs(r.break_even_hours)} hrs{r.sales_rep_since ? ` · since ${r.sales_rep_since}` : ""}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-mono font-bold" data-testid={`rep-week-pay-${r.id}`}>{money(cw.pay)} <span className="font-sans font-normal text-xs text-slate-500">· {hrs(cw.hours)} hrs</span></div>
                      <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${over ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-amber-100 text-amber-800 border-amber-200"}`}>
                        {over ? `Commission · +${hrs(cw.hours_over_break_even)} hrs` : `Draw · ${hrs(cw.hours_to_break_even)} hrs short`}
                      </span>
                      {cw.pending_hours > 0 && <div className="text-[11px] text-amber-700 mt-0.5">+{hrs(cw.pending_hours)} hrs pending approval</div>}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-slate-700">
                      {(r.last_weeks || []).map((w) => <div key={w.week_start} className={w.applies === "commission" ? "text-emerald-700" : ""}>{money(w.pay)} <span className="text-slate-400 font-sans">({hrs(w.hours)}h)</span></div>)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{r.active_project_count} · {r.active_worker_count}</td>
                    <td className="px-4 py-3">
                      <button type="button" onClick={() => isAdmin && toggleReview(r)} disabled={!isAdmin || busyId === r.id} data-testid={`rep-review-toggle-${r.id}`}
                              title={isAdmin ? "Click to switch" : ""}
                              className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold border ${r.review_required ? "bg-amber-100 text-amber-900 border-amber-300" : "bg-teal-100 text-teal-900 border-teal-300"} ${isAdmin ? "hover:ring-2 hover:ring-slate-300" : "cursor-default"} disabled:opacity-60`}>
                        {r.review_required ? "Required" : "Auto-send · FYI only"}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      {r.pending_reviews > 0
                        ? <div className="flex flex-col gap-1 items-start" data-testid={`rep-pending-${r.id}`}>
                            {r.pending_proposals > 0 && <Link to="/proposals?filter=review" data-testid={`rep-pending-proposals-${r.id}`} className="inline-block px-2 py-0.5 rounded-full text-xs font-bold border bg-amber-100 text-amber-900 border-amber-300">{r.pending_proposals} proposal{r.pending_proposals > 1 ? "s" : ""}</Link>}
                            {r.pending_packets > 0 && <Link to="/packets?filter=review" data-testid={`rep-pending-packets-${r.id}`} className="inline-block px-2 py-0.5 rounded-full text-xs font-bold border bg-amber-100 text-amber-900 border-amber-300">{r.pending_packets} packet{r.pending_packets > 1 ? "s" : ""}</Link>}
                          </div>
                        : <span className="text-xs text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {r.login?.exists
                        ? <span className={`inline-block px-2 py-0.5 rounded-full font-semibold border ${r.login.activated ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{r.login.activated ? "Active" : "Sent · not yet set up"}</span>
                        : <span className="text-slate-400">No login</span>}
                      {r.sales_login_sent_at && <div className="text-slate-400 mt-0.5">sent {String(r.sales_login_sent_at).slice(0, 10)}</div>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {isAdmin && (
                        <button onClick={() => sendLogin(r)} disabled={busyId === r.id || !r.email} data-testid={`rep-send-login-${r.id}`} title={r.email ? "Email portal login" : "Add an email to this worker first"}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs font-bold disabled:opacity-40">
                          <PaperPlaneTilt size={13} weight="bold" /> {r.login?.exists ? "Resend login" : "Send login"}
                        </button>
                      )}
                      <button onClick={() => setOpenId(open ? null : r.id)} data-testid={`rep-toggle-weeks-${r.id}`}
                              className="ml-2 inline-flex items-center gap-1 px-2.5 py-1 rounded border border-emerald-300 text-emerald-800 hover:bg-emerald-50 text-xs font-bold">
                        {open ? <CaretUp size={13} weight="bold" /> : <CaretDown size={13} weight="bold" />} Weeks
                      </button>
                    </td>
                  </tr>
                  {open && <tr><td colSpan={9} className="p-0"><RepWeeks repId={r.id} /></td></tr>}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
